import { useEffect, useRef } from 'react'
import Badge from 'react-bootstrap/Badge'
import Card from 'react-bootstrap/Card'
import ListGroup from 'react-bootstrap/ListGroup'
import Spinner from 'react-bootstrap/Spinner'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { GroupDetail, Receipt } from '../../../../types'
import { useCurrencies } from '../../hooks/useCurrencies'
import { seRozpoznava, stavKontroly } from '../../hooks/useReceipts'
import { formatDateTime } from '../../utils/format'
import { exponentOf, formatMoney } from '../../utils/money'
import { popisFotek } from './ReceiptUploadGrouping'

export type ReceiptReviewSectionProps = {
    group: GroupDetail
    /** Fronta účtenek skupiny - nahrané účtenky bez útraty. */
    receipts: Receipt[]
}

/**
 * Účtenky ke kontrole - nahrané účtenky, ze kterých ještě nevznikla útrata.
 *
 * Rozpoznávání i čekání na AI asistenta běží na pozadí; sekce jen ukazuje, jak
 * daleko která účtenka je, a odkazuje na její kontrolu. Účtenka, která
 * doběhne, zatímco je aplikace otevřená, se ohlásí upozorněním.
 */
export function ReceiptReviewSection({ group, receipts }: ReceiptReviewSectionProps) {
    const { data: currencies } = useCurrencies()
    useOhlaseniHotovych(receipts)

    if (receipts.length === 0) {
        return null
    }

    return (
        <Card className="mb-3" data-testid="ke-kontrole">
            <Card.Header className="d-flex justify-content-between align-items-center gap-2">
                <span className="fw-semibold text-nowrap">
                    <i className="bi bi-receipt me-1" aria-hidden="true" />
                    Ke kontrole ({receipts.length})
                </span>
                <span className="small text-secondary d-none d-sm-inline">Nepotvrzené se po 14 dnech smažou</span>
            </Card.Header>
            <ListGroup variant="flush">
                {receipts.map((receipt) => {
                    const mena = receipt.detectedCurrencyCode ?? group.baseCurrencyCode
                    const prirazeno = receipt.items.filter((item) => item.memberIds.length > 0).length
                    const zpracovava = stavKontroly(receipt) === 'ZPRACOVAVA'

                    return (
                        <ListGroup.Item
                            key={receipt.id}
                            action
                            as={Link}
                            to={`/skupiny/${group.id}/uctenky/${receipt.id}`}
                            className="d-flex align-items-center gap-2"
                            data-testid="uctenka-ke-kontrole"
                        >
                            {/* Fotka se odkazuje přímo URL - přes fetch by se musela převádět na blob. */}
                            <img
                                src={`/api/receipts/${receipt.id}/file?variant=thumb`}
                                alt=""
                                className="rounded border flex-shrink-0"
                                style={{ width: '3.5rem', height: '3.5rem', objectFit: 'cover' }}
                            />
                            <div className="flex-grow-1 min-w-0">
                                <div className="d-flex justify-content-between gap-2">
                                    <span className="fw-semibold text-truncate">
                                        {receipt.detectedTitle ?? (zpracovava ? 'Účtenka' : 'Nerozpoznaný obchod')}
                                    </span>
                                    {receipt.detectedTotal !== null && receipt.detectedTotal !== undefined && !zpracovava && (
                                        <span className="fw-semibold text-nowrap">
                                            {formatMoney(receipt.detectedTotal, mena, exponentOf(currencies, mena))}
                                        </span>
                                    )}
                                </div>
                                <div className="small text-secondary">
                                    Nahráno {formatDateTime(receipt.createdAt)}
                                    {receipt.pageCount > 1 && ` · ${popisFotek(receipt.pageCount)}`}
                                </div>
                                <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
                                    <StavUctenky receipt={receipt} />
                                    {!zpracovava && receipt.items.length > 0 && (
                                        <span className="small text-secondary">
                                            Přiřazeno {prirazeno} z {receipt.items.length} {receipt.items.length === 1 ? 'položky' : 'položek'}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <i className="bi bi-chevron-right text-secondary flex-shrink-0" aria-hidden="true" />
                        </ListGroup.Item>
                    )
                })}
            </ListGroup>
        </Card>
    )
}

/** Stav účtenky ve frontě ke kontrole. */
export function StavUctenky({ receipt }: { receipt: Receipt }) {
    const stav = stavKontroly(receipt)

    if (stav === 'ZPRACOVAVA') {
        const popis = seRozpoznava(receipt)
            ? receipt.awaitingMcp
                ? 'Rozpoznávám · čeká i na AI asistenta'
                : 'Rozpoznávám'
            : receipt.analysisStatus === 'AWAITING_MCP'
              ? 'Čeká na AI asistenta'
              : receipt.analysisStatus === 'FAILED'
                ? 'Nerozpoznáno · čeká ještě na AI asistenta'
                : 'Rozpoznáno · čeká ještě na AI asistenta'
        return (
            <Badge bg="info" text="dark">
                {seRozpoznava(receipt) && <Spinner animation="border" size="sm" className="me-1" style={{ width: '0.7rem', height: '0.7rem' }} />}
                {popis}
            </Badge>
        )
    }
    if (stav === 'KE_KONTROLE') {
        return <Badge bg="success">Připraveno ke kontrole</Badge>
    }
    if (stav === 'NEROZPOZNANO') {
        return (
            <Badge bg="warning" text="dark" title={receipt.analysisError ?? undefined}>
                Nerozpoznáno — doplňte ručně
            </Badge>
        )
    }
    return <Badge bg="secondary">Doplňte ručně</Badge>
}

/**
 * Ohlásí účtenky, které během otevřené aplikace dočetl server nebo asistent.
 * Účtenky, které byly hotové už při načtení, se neohlašují.
 */
function useOhlaseniHotovych(receipts: Receipt[]) {
    const zpracovavane = useRef<Set<string> | undefined>(undefined)

    useEffect(() => {
        const ted = new Set(receipts.filter((receipt) => stavKontroly(receipt) === 'ZPRACOVAVA').map((receipt) => receipt.id))
        const predtim = zpracovavane.current
        zpracovavane.current = ted
        if (!predtim) {
            return
        }

        for (const receipt of receipts) {
            if (predtim.has(receipt.id) && !ted.has(receipt.id)) {
                toast.info(`Účtenka ${receipt.detectedTitle ? `„${receipt.detectedTitle}" ` : ''}je připravená ke kontrole.`)
            }
        }
    }, [receipts])
}
